import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import toast from "react-hot-toast";

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-IN") : "—";
  } catch {
    return "—";
  }
};

const toDateInput = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : new Date();
    return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : "";
  } catch {
    return "";
  }
};

const getTime = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  } catch {
    return 0;
  }
};

const getFY = (dateValue) => {
  const d = dateValue ? new Date(dateValue) : new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${String(start).slice(-2)}${String(start + 1).slice(-2)}`;
};

const makeInvoiceNo = (appId = "") => `TF/${getFY()}/${String(appId).slice(0, 6).toUpperCase()}`;
const makeReceiptNo = (appId = "") => `TFR/${getFY()}/${String(appId).slice(0, 6).toUpperCase()}`;

const feeBreakup = (gross) => {
  const amount = Number(gross || 0);
  const taxable = Number((amount / 1.18).toFixed(2));
  const gst = Number((amount - taxable).toFixed(2));
  return { taxable, gst, gross: amount };
};

function Badge({ children }) {
  const s = String(children || "").toUpperCase();
  let cls = "bg-amber-100 text-amber-700";
  if (s.includes("PAID") || s.includes("APPROVED") || s.includes("POSTED") || s.includes("VERIFIED"))
    cls = "bg-emerald-100 text-emerald-700";
  if (s.includes("INVOICE") || s.includes("OLD"))
    cls = "bg-purple-100 text-purple-700";
  if (s.includes("CORRECTION") || s.includes("REJECT"))
    cls = "bg-red-100 text-red-700";

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-black ${cls}`}>
      {children || "—"}
    </span>
  );
}

export default function TenderFeeAccounting() {
  return (
    <AccountingShell title="Tender Fee Accounting">
      <TenderFeeAccountingContent />
    </AccountingShell>
  );
}

function TenderFeeAccountingContent({ me = {} }) {
  const [apps, setApps] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [fyFilter, setFyFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("NEWEST");

  const load = async () => {
    const [appSnap, tenderSnap] = await Promise.all([
      getDocs(collection(db, "tender_applications")),
      getDocs(collection(db, "tenders")),
    ]);
    setApps(appSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTenders(tenderSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    let list = apps.map((a) => {
      const tender = tenders.find((t) => t.id === a.tenderId) || {};
      const fee = Number(a.tenderFeeAmount || 0);
      const b = feeBreakup(fee);
      const invoiceNo = a.tenderFeeInvoiceNo || a.oldTenderFeeInvoiceNo || "";
      const invoiceDate =
        a.tenderFeeInvoiceDate ||
        a.oldTenderFeeInvoiceDate ||
        a.tenderFeeInvoiceGeneratedAt ||
        a.createdAt ||
        a.appliedAt ||
        a.submittedAt;
      const invoiceFY = a.tenderFeeInvoiceFY || getFY(toDateInput(invoiceDate));

      return {
        ...a,
        tenderTitle: tender.title || tender.projectName || a.tenderTitle || "—",
        projectName: tender.ownerCompany || tender.pacsName || tender.projectName || tender.title || "—",
        tenderAmount: Number(tender.baseAmount || tender.amount || 0),
        l1Amount: Number(tender.awardedAmount || tender.tenderAwardAmount || tender.awardedValue || a.l1Amount || 0),
        vendorDisplay: a.vendorName || a.companyName || a.contractorName || a.vendorUid || "—",
        tenderFeeAmount: fee,
        tenderFeeTaxableAmount: Number(a.tenderFeeTaxableAmount || b.taxable),
        tenderFeeGstAmount: Number(a.tenderFeeGstAmount || b.gst),
        tenderFeeGrossAmount: Number(a.tenderFeeGrossAmount || b.gross),
        tenderFeeInvoiceNo: invoiceNo,
        tenderFeeInvoiceDate: invoiceDate,
        tenderFeeInvoiceFY: invoiceFY,
        hasInvoice: Boolean(invoiceNo),
        oldInvoiceVerified: Boolean(a.tenderFeeOldInvoiceVerified),
        accountantVerified: Boolean(a.tenderFeeAccountantVerified),
        ceoVerified: a.accountingTenderFeeStatus === "CEO_APPROVED",
        feeStatus: String(a.tenderFeeStatus || "pending").toLowerCase(),
        accountingStatus: a.accountingTenderFeeStatus || (invoiceNo ? "OLD_INVOICE_PENDING_VERIFY" : "PENDING"),
        invoiceStatus: a.tenderFeeInvoiceStatus || (invoiceNo ? "OLD_INVOICE_PENDING_VERIFY" : "NOT_GENERATED"),
        paymentStatus: a.tenderFeePaymentStatus || "PENDING",
        invoiceDebit: Number(a.tenderFeeInvoiceDebit || 0),
        paymentCredit: Number(a.tenderFeePaymentCredit || 0),
        balance: Number(a.tenderFeeInvoiceDebit || 0) - Number(a.tenderFeePaymentCredit || 0),
        createdTime: getTime(a.createdAt || a.appliedAt || a.submittedAt),
      };
    });

    if (fyFilter !== "ALL") list = list.filter((r) => r.tenderFeeInvoiceFY === fyFilter);

    if (statusFilter !== "ALL") {
      list = list.filter(
        (r) =>
          r.accountingStatus === statusFilter ||
          r.invoiceStatus === statusFilter ||
          r.paymentStatus === statusFilter ||
          r.feeStatus === statusFilter.toLowerCase()
      );
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        [
          r.vendorDisplay,
          r.vendorUid,
          r.tenderId,
          r.tenderTitle,
          r.projectName,
          r.id,
          r.tenderFeeInvoiceNo,
          r.tenderFeeReceiptNo,
          r.tenderFeePayment?.reference,
          r.tenderFeePaymentUtr,
        ].join(" ").toLowerCase().includes(s)
      );
    }

    if (sortBy === "NEWEST") list.sort((a, b) => b.createdTime - a.createdTime);
    if (sortBy === "OLDEST") list.sort((a, b) => a.createdTime - b.createdTime);
    if (sortBy === "AMOUNT_HIGH") list.sort((a, b) => b.tenderFeeAmount - a.tenderFeeAmount);

    return list;
  }, [apps, tenders, search, statusFilter, fyFilter, sortBy]);

  const fyOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.tenderFeeInvoiceFY).filter(Boolean));
    return ["ALL", ...Array.from(set).sort().reverse()];
  }, [rows]);

  const totals = useMemo(
    () => ({
      total: rows.length,
      fee: rows.reduce((s, r) => s + Number(r.tenderFeeAmount || 0), 0),
      invoices: rows.filter((r) => r.hasInvoice).length,
      accountantVerified: rows.filter((r) => r.accountantVerified).length,
      ceoApproved: rows.filter((r) => r.ceoVerified).length,
      debit: rows.reduce((s, r) => s + Number(r.invoiceDebit || 0), 0),
      credit: rows.reduce((s, r) => s + Number(r.paymentCredit || 0), 0),
      balance: rows.reduce((s, r) => s + Number(r.balance || 0), 0),
    }),
    [rows]
  );

  const safeAddLedger = async (payload) => {
    const q = query(
      collection(db, "contractor_ledgers"),
      where("applicationId", "==", payload.applicationId),
      where("type", "==", payload.type),
      where("voucherNo", "==", payload.voucherNo)
    );
    const snap = await getDocs(q);
    if (!snap.empty) return false;

    await addDoc(collection(db, "contractor_ledgers"), {
      ...payload,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
      createdByRole: me.role || "",
    });
    return true;
  };

  const editInvoiceDetails = async (a) => {
    const invoiceNo = window.prompt("Tender Fee Invoice No:", a.tenderFeeInvoiceNo || makeInvoiceNo(a.id));
    if (!invoiceNo) return;

    const invoiceDate = window.prompt("Invoice Date YYYY-MM-DD:", toDateInput(a.tenderFeeInvoiceDate));
    if (!invoiceDate) return;

    const amountText = window.prompt("Gross Tender Fee Amount:", String(a.tenderFeeAmount || 2950));
    if (!amountText) return;

    const amount = Number(amountText);
    const b = feeBreakup(amount);

    await updateDoc(doc(db, "tender_applications", a.id), {
      tenderFeeAmount: amount,
      tenderFeeInvoiceNo: invoiceNo,
      tenderFeeInvoiceDate: invoiceDate,
      tenderFeeInvoiceFY: getFY(invoiceDate),
      tenderFeeTaxableAmount: b.taxable,
      tenderFeeGstAmount: b.gst,
      tenderFeeGrossAmount: b.gross,
      tenderFeeInvoiceEditedBy: me.uid || "",
      tenderFeeInvoiceEditedAt: serverTimestamp(),
    });

    toast.success("Invoice detail updated.");
    load();
  };

  const verifyFeePaid = async (a) => {
    const ok = window.confirm(`${a.vendorDisplay} ki tender fee paid verify karni hai?`);
    if (!ok) return;

    await updateDoc(doc(db, "tender_applications", a.id), {
      tenderFeeStatus: "paid",
      tenderFeeVerifiedFromAccounting: true,
      tenderFeeVerifiedWithoutReceipt: !(a.tenderFeePayment?.receiptUrl || a.receiptUrl),
      tenderFeeAccountantVerified: true,
      tenderFeeAccountantVerifiedBy: me.uid || "",
      tenderFeeAccountantVerifiedAt: serverTimestamp(),
      accountingTenderFeeStatus: a.hasInvoice ? "INVOICE_GENERATED_CEO_PENDING" : "ACCOUNTANT_VERIFIED",
      accountingRemark: "",
    });

    toast.success("Tender fee paid verified by Accountant.");
    load();
  };

  const verifyOldInvoice = async (a) => {
    const invoiceNo = a.tenderFeeInvoiceNo || window.prompt("Old invoice no:", "");
    if (!invoiceNo) return;

    const invoiceDate = window.prompt("Old invoice date YYYY-MM-DD:", toDateInput(a.tenderFeeInvoiceDate));
    if (!invoiceDate) return;

    const amount = Number(a.tenderFeeGrossAmount || a.tenderFeeAmount || 0);
    const b = feeBreakup(amount);

    await updateDoc(doc(db, "tender_applications", a.id), {
      tenderFeeInvoiceNo: invoiceNo,
      tenderFeeInvoiceDate: invoiceDate,
      tenderFeeInvoiceFY: getFY(invoiceDate),
      tenderFeeInvoiceStatus: "GENERATED",
      tenderFeeTaxableAmount: b.taxable,
      tenderFeeGstAmount: b.gst,
      tenderFeeGrossAmount: b.gross,
      tenderFeeOldInvoiceVerified: true,
      tenderFeeAccountantVerified: true,
      tenderFeeOldInvoiceVerifiedBy: me.uid || "",
      tenderFeeOldInvoiceVerifiedAt: serverTimestamp(),
      tenderFeeAccountantVerifiedBy: me.uid || "",
      tenderFeeAccountantVerifiedAt: serverTimestamp(),
      accountingTenderFeeStatus: "INVOICE_GENERATED_CEO_PENDING",
    });

    toast.success("Old invoice verified by Accountant. CEO pending.");
    load();
  };

  const generateInvoice = async (a) => {
    if (!a.accountantVerified && a.accountingStatus !== "ACCOUNTANT_VERIFIED") {
      return toast.error("Pehle Accountant fee verify kare.");
    }

    const invoiceNo = window.prompt("Tender Fee Invoice No:", a.tenderFeeInvoiceNo || makeInvoiceNo(a.id)) || makeInvoiceNo(a.id);
    const invoiceDate = window.prompt("Invoice Date YYYY-MM-DD:", toDateInput(new Date())) || toDateInput(new Date());
    const b = feeBreakup(a.tenderFeeAmount);

    await updateDoc(doc(db, "tender_applications", a.id), {
      tenderFeeInvoiceNo: invoiceNo,
      tenderFeeInvoiceDate: invoiceDate,
      tenderFeeInvoiceFY: getFY(invoiceDate),
      tenderFeeInvoiceStatus: "GENERATED",
      tenderFeeInvoiceGeneratedAt: serverTimestamp(),
      tenderFeeInvoiceGeneratedBy: me.uid || "",
      tenderFeeTaxableAmount: b.taxable,
      tenderFeeGstAmount: b.gst,
      tenderFeeGrossAmount: b.gross,
      accountingTenderFeeStatus: "INVOICE_GENERATED_CEO_PENDING",
      tenderFeeInvoiceDebit: 0,
      tenderFeePaymentCredit: 0,
      tenderFeeBalance: 0,
    });

    toast.success("Invoice generated. CEO approval pending.");
    load();
  };

  const ceoApproveInvoice = async (a) => {
    if (!a.accountantVerified) return toast.error("Pehle Accountant verify kare.");
    if (!a.tenderFeeInvoiceNo) return toast.error("Invoice missing hai.");

    const amount = Number(a.tenderFeeGrossAmount || a.tenderFeeAmount || 0);
    const b = feeBreakup(amount);

    await updateDoc(doc(db, "tender_applications", a.id), {
      accountingTenderFeeStatus: "CEO_APPROVED",
      tenderFeeAccountingStatus: "INVOICE_LEDGER_POSTED",
      accountingTenderFeeCeoApprovedBy: me.uid || "",
      accountingTenderFeeCeoApprovedAt: serverTimestamp(),
      tenderFeeInvoiceDebit: amount,
      tenderFeeBalance: amount - Number(a.paymentCredit || 0),
    });

    await safeAddLedger({
      vendorUid: a.vendorUid || "",
      vendorName: a.vendorDisplay || "",
      tenderId: a.tenderId || "",
      applicationId: a.id,
      voucherNo: a.tenderFeeInvoiceNo,
      invoiceNo: a.tenderFeeInvoiceNo,
      type: "TENDER_FEE_INVOICE",
      debit: amount,
      credit: 0,
      taxableAmount: b.taxable,
      gstRate: 18,
      gstAmount: b.gst,
      grossAmount: b.gross,
      narration: `Tender Fee Invoice - ${a.tenderTitle}`,
      serviceName: "Tender Fee",
      status: "POSTED",
    });

    toast.success("CEO approved. Invoice posted in DEBIT ledger.");
    load();
  };

  const verifyPaymentAndReceipt = async (a) => {
    if (a.accountingStatus !== "CEO_APPROVED") return toast.error("Pehle CEO approve kare.");

    const utr = window.prompt("Payment UTR / Reference No:", a.tenderFeePayment?.reference || a.tenderFeePaymentUtr || "");
    if (!utr) return;

    const receiptNo = a.tenderFeeReceiptNo || makeReceiptNo(a.id);
    const amount = Number(a.tenderFeeGrossAmount || a.tenderFeeAmount || 0);

    await updateDoc(doc(db, "tender_applications", a.id), {
      tenderFeePaymentStatus: "PAYMENT_VERIFIED",
      tenderFeePaymentVerifiedBy: me.uid || "",
      tenderFeePaymentVerifiedAt: serverTimestamp(),
      tenderFeePaymentUtr: utr,
      tenderFeeReceiptNo: receiptNo,
      tenderFeeReceiptDate: toDateInput(new Date()),
      tenderFeeReceiptGeneratedAt: serverTimestamp(),
      tenderFeePaymentCredit: amount,
      tenderFeeBalance: 0,
      tenderFeeAccountingStatus: "PAYMENT_LEDGER_POSTED",
      tenderFeeTallyLedgerFixed: true,
    });

    await safeAddLedger({
      vendorUid: a.vendorUid || "",
      vendorName: a.vendorDisplay || "",
      tenderId: a.tenderId || "",
      applicationId: a.id,
      voucherNo: `PAY-${a.tenderFeeInvoiceNo}`,
      receiptNo,
      referenceNo: utr,
      type: "TENDER_FEE_PAYMENT_RECEIVED",
      debit: 0,
      credit: amount,
      invoiceNo: a.tenderFeeInvoiceNo,
      adjustedAgainstInvoice: a.tenderFeeInvoiceNo,
      narration: `Tender fee payment received against invoice ${a.tenderFeeInvoiceNo}`,
      paymentUtr: utr,
      serviceName: "Tender Fee Payment",
      status: "POSTED",
    });

    toast.success("Payment verified by Accountant. Receipt no generated + CREDIT ledger posted.");
    load();
  };

  const correction = async (a) => {
    const remark = window.prompt("Correction remark:");
    if (!remark) return;

    await updateDoc(doc(db, "tender_applications", a.id), {
      accountingTenderFeeStatus: "CORRECTION_REQUIRED",
      accountingRemark: remark,
      correctionRaisedAt: serverTimestamp(),
      correctionRaisedBy: me.uid || "",
    });

    toast.success("Correction sent.");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-7 gap-3">
        <Card title="Applications" value={totals.total} />
        <Card title="Tender Fee" value={INR(totals.fee)} />
        <Card title="Invoices" value={totals.invoices} green />
        <Card title="Accountant Verified" value={totals.accountantVerified} green />
        <Card title="CEO Approved" value={totals.ceoApproved} green />
        <Card title="Debit" value={INR(totals.debit)} red />
        <Card title="Credit" value={INR(totals.credit)} green />
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="input" placeholder="Search vendor / invoice / receipt / UTR..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="input" value={fyFilter} onChange={(e) => setFyFilter(e.target.value)}>
            {fyOptions.map((f) => <option key={f} value={f}>{f === "ALL" ? "All FY" : `FY ${f}`}</option>)}
          </select>
          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="pending">Fee Pending</option>
            <option value="paid">Fee Paid</option>
            <option value="ACCOUNTANT_VERIFIED">Accountant Verified</option>
            <option value="OLD_INVOICE_PENDING_VERIFY">Old Invoice Pending</option>
            <option value="INVOICE_GENERATED_CEO_PENDING">CEO Pending</option>
            <option value="CEO_APPROVED">CEO Approved</option>
            <option value="PAYMENT_VERIFIED">Payment Verified</option>
            <option value="CORRECTION_REQUIRED">Correction</option>
          </select>
          <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="NEWEST">Newest</option>
            <option value="OLDEST">Oldest</option>
            <option value="AMOUNT_HIGH">Amount High</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <h2 className="text-lg font-black text-center mb-1">Tender Fee Accounting Register</h2>
        <p className="text-xs text-slate-500 text-center mb-4">
          Accountant: fee verify + invoice generate/edit + payment verify | CEO: approval only
        </p>

        <div className="overflow-auto">
          <table className="min-w-[1800px] w-full text-[11px] border border-slate-300">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2 border">Sr</th>
                <th className="p-2 border text-left">Application</th>
                <th className="p-2 border text-left">Vendor</th>
                <th className="p-2 border text-left">Tender / Project</th>
                <th className="p-2 border text-right">Fee</th>
                <th className="p-2 border text-right">Taxable</th>
                <th className="p-2 border text-right">GST</th>
                <th className="p-2 border text-left">Invoice No / Date</th>
                <th className="p-2 border text-left">Receipt / UTR</th>
                <th className="p-2 border text-right">Debit</th>
                <th className="p-2 border text-right">Credit</th>
                <th className="p-2 border text-right">Balance</th>
                <th className="p-2 border text-center">Accountant</th>
                <th className="p-2 border text-center">CEO</th>
                <th className="p-2 border text-center">Payment</th>
                <th className="p-2 border text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((a, i) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>
                  <td className="p-2 border font-black">{a.id}<div className="text-[10px] text-slate-500">{fmtDate(a.createdAt || a.appliedAt)}</div></td>
                  <td className="p-2 border font-black">{a.vendorDisplay}<div className="text-[10px] text-slate-500">UID: {a.vendorUid || "—"}</div></td>
                  <td className="p-2 border"><div className="font-bold">{a.tenderTitle}</div><div className="text-[10px] text-slate-500">{a.projectName}</div></td>
                  <td className="p-2 border text-right font-black">{INR(a.tenderFeeAmount)}</td>
                  <td className="p-2 border text-right">{INR(a.tenderFeeTaxableAmount)}</td>
                  <td className="p-2 border text-right">{INR(a.tenderFeeGstAmount)}</td>
                  <td className="p-2 border font-black">
                    {a.tenderFeeInvoiceNo || "—"}
                    <div className="text-[10px] text-slate-500">{fmtDate(a.tenderFeeInvoiceDate)}</div>
                    {a.hasInvoice && !a.oldInvoiceVerified && <div className="text-[10px] text-purple-700">OLD VERIFY PENDING</div>}
                  </td>
                  <td className="p-2 border font-black">
                    {a.tenderFeeReceiptNo || "—"}
                    {a.tenderFeePaymentUtr && <div className="text-[10px] text-slate-500">UTR: {a.tenderFeePaymentUtr}</div>}
                  </td>
                  <td className="p-2 border text-right text-red-600 font-black">{a.invoiceDebit ? INR(a.invoiceDebit) : "—"}</td>
                  <td className="p-2 border text-right text-emerald-700 font-black">{a.paymentCredit ? INR(a.paymentCredit) : "—"}</td>
                  <td className="p-2 border text-right font-black">{INR(a.balance)}</td>
                  <td className="p-2 border text-center"><Badge>{a.accountantVerified ? "VERIFIED" : "PENDING"}</Badge></td>
                  <td className="p-2 border text-center"><Badge>{a.ceoVerified ? "CEO_APPROVED" : "PENDING"}</Badge></td>
                  <td className="p-2 border text-center"><Badge>{a.paymentStatus}</Badge></td>

                  <td className="p-2 border">
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button className="btn-red" onClick={() => correction(a)}>Correction</button>

                      {["ACCOUNTANT", "ADMIN"].includes(me.role) && (
                        <button className="btn-gray" onClick={() => editInvoiceDetails(a)}>Edit</button>
                      )}

                      {["ACCOUNTANT", "ADMIN"].includes(me.role) && !a.accountantVerified && (
                        <button className="btn" onClick={() => verifyFeePaid(a)}>Verify Fee</button>
                      )}

                      {["ACCOUNTANT", "ADMIN"].includes(me.role) && a.hasInvoice && !a.oldInvoiceVerified && (
                        <button className="btn-dark" onClick={() => verifyOldInvoice(a)}>Verify Old</button>
                      )}

                      {["ACCOUNTANT", "ADMIN"].includes(me.role) && a.accountantVerified && !a.hasInvoice && (
                        <button className="btn-dark" onClick={() => generateInvoice(a)}>Gen Invoice</button>
                      )}

                      {["CEO", "ADMIN"].includes(me.role) && a.accountantVerified && a.hasInvoice && !a.ceoVerified && (
                        <button className="btn" onClick={() => ceoApproveInvoice(a)}>CEO Approve</button>
                      )}

                      {["ACCOUNTANT", "ADMIN"].includes(me.role) && a.ceoVerified && a.paymentStatus !== "PAYMENT_VERIFIED" && (
                        <button className="btn" onClick={() => verifyPaymentAndReceipt(a)}>Verify Pay</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {!rows.length && (
                <tr>
                  <td colSpan={16} className="p-8 text-center text-slate-500">
                    No tender fee records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .input{border:1px solid #cbd5e1;border-radius:12px;padding:9px 12px;font-size:12px;font-weight:700;width:100%}
        .btn{background:#059669;color:white;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
        .btn-dark{background:#0f172a;color:white;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
        .btn-red{background:#dc2626;color:white;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
        .btn-gray{background:#e2e8f0;color:#0f172a;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
      `}</style>
    </div>
  );
}

function Card({ title, value, green, red }) {
  return (
    <div className="bg-white rounded-2xl border p-4 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2 className={`text-lg font-black mt-2 ${green ? "text-emerald-700" : red ? "text-red-600" : "text-slate-900"}`}>
        {value}
      </h2>
    </div>
  );
}